// Dependency-free helpers translating between a friendly schedule builder and
// 5-field cron expressions. The builder only emits common shapes (daily / weekly
// / monthly / interval); anything else round-trips as a raw "advanced" cron.
import type { ScheduledJob, ScheduleType } from '@/services/schedulerService';

export type Frequency = 'daily' | 'weekly' | 'monthly' | 'interval' | 'advanced';

export interface ScheduleForm {
  frequency: Frequency;
  time: string; // 'HH:mm'
  daysOfWeek: number[]; // 0=Sun … 6=Sat
  dayOfMonth: number; // 1..28
  intervalValue: number;
  intervalUnit: 'minutes' | 'hours';
  cron: string;
}

export interface ScheduleResult {
  schedule_type: ScheduleType;
  cron_expression?: string;
  interval_seconds?: number;
}

const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_NAMES: Record<string, number> = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
const pad = (n: number) => String(n).padStart(2, '0');

function parseDow(field: string): number[] | null {
  if (!field || field === '*') return null;
  const out = new Set<number>();
  for (const part of field.split(',')) {
    const range = part.split('-');
    const toNum = (s: string): number | null => {
      const up = s.toUpperCase();
      if (up in DOW_NAMES) return DOW_NAMES[up];
      const n = Number(s);
      if (!Number.isInteger(n)) return null;
      return n === 7 ? 0 : n;
    };
    if (range.length === 2) {
      const a = toNum(range[0]);
      const b = toNum(range[1]);
      if (a == null || b == null) return null;
      for (let i = a; i <= b; i++) out.add(i % 7);
    } else {
      const n = toNum(part);
      if (n == null) return null;
      out.add(n);
    }
  }
  return [...out].sort((x, y) => x - y);
}

export function jobToForm(job: ScheduledJob): ScheduleForm {
  const base: ScheduleForm = {
    frequency: 'advanced',
    time: '09:00',
    daysOfWeek: [1, 2, 3, 4, 5],
    dayOfMonth: 1,
    intervalValue: 30,
    intervalUnit: 'minutes',
    cron: job.cron_expression || '',
  };

  if (job.schedule_type === 'interval' && job.interval_seconds) {
    base.frequency = 'interval';
    if (job.interval_seconds % 3600 === 0) {
      base.intervalUnit = 'hours';
      base.intervalValue = job.interval_seconds / 3600;
    } else {
      base.intervalUnit = 'minutes';
      base.intervalValue = Math.max(1, Math.round(job.interval_seconds / 60));
    }
    return base;
  }

  const fields = (job.cron_expression || '').trim().split(/\s+/);
  if (job.schedule_type === 'cron' && fields.length === 5) {
    const [min, hour, dom, mon, dow] = fields;
    if (/^\*\/\d+$/.test(min) && hour === '*' && dom === '*' && mon === '*' && dow === '*') {
      base.frequency = 'interval';
      base.intervalUnit = 'minutes';
      base.intervalValue = Number(min.slice(2));
      return base;
    }
    if (min !== '*' && /^\*\/\d+$/.test(hour) && dom === '*' && mon === '*' && dow === '*') {
      base.frequency = 'interval';
      base.intervalUnit = 'hours';
      base.intervalValue = Number(hour.slice(2));
      return base;
    }
    const minN = Number(min);
    const hourN = Number(hour);
    const fixedTime = Number.isInteger(minN) && Number.isInteger(hourN);
    if (fixedTime) base.time = `${pad(hourN)}:${pad(minN)}`;
    const dowList = parseDow(dow);
    if (fixedTime && dom === '*' && mon === '*' && dowList && dowList.length) {
      base.frequency = 'weekly';
      base.daysOfWeek = dowList;
      return base;
    }
    if (fixedTime && /^\d+$/.test(dom) && mon === '*' && (dow === '*' || dow === '?')) {
      base.frequency = 'monthly';
      base.dayOfMonth = Number(dom);
      return base;
    }
    if (fixedTime && dom === '*' && mon === '*' && (dow === '*' || dow === '?')) {
      base.frequency = 'daily';
      return base;
    }
  }
  return base;
}

export function formToSchedule(f: ScheduleForm): ScheduleResult {
  const [h, m] = (f.time || '00:00').split(':').map((x) => Number(x) || 0);
  switch (f.frequency) {
    case 'daily':
      return { schedule_type: 'cron', cron_expression: `${m} ${h} * * *` };
    case 'weekly': {
      const days = (f.daysOfWeek.length ? [...f.daysOfWeek] : [1]).sort((a, b) => a - b).join(',');
      return { schedule_type: 'cron', cron_expression: `${m} ${h} * * ${days}` };
    }
    case 'monthly': {
      const dom = Math.min(28, Math.max(1, f.dayOfMonth || 1));
      return { schedule_type: 'cron', cron_expression: `${m} ${h} ${dom} * *` };
    }
    case 'interval': {
      const secs = f.intervalUnit === 'hours' ? f.intervalValue * 3600 : f.intervalValue * 60;
      return { schedule_type: 'interval', interval_seconds: Math.max(60, secs) };
    }
    case 'advanced':
    default:
      return { schedule_type: 'cron', cron_expression: (f.cron || '').trim() };
  }
}

export function describeJob(job: Pick<ScheduledJob, 'schedule_type' | 'cron_expression' | 'interval_seconds' | 'timezone'>): string {
  const tz = job.timezone ? ` (${job.timezone})` : '';
  if (job.schedule_type === 'interval' && job.interval_seconds) {
    return `Every ${humanInterval(job.interval_seconds)}`;
  }
  const fields = (job.cron_expression || '').trim().split(/\s+/);
  if (fields.length !== 5) return job.cron_expression || job.schedule_type;
  const [min, hour, dom, mon, dow] = fields;
  if (/^\*\/\d+$/.test(min) && hour === '*' && dom === '*' && mon === '*' && dow === '*') return `Every ${min.slice(2)} minutes`;
  if (min !== '*' && /^\*\/\d+$/.test(hour) && dom === '*' && mon === '*' && dow === '*') return `Every ${hour.slice(2)} hours`;
  const minN = Number(min);
  const hourN = Number(hour);
  const at = Number.isInteger(minN) && Number.isInteger(hourN) ? `${pad(hourN)}:${pad(minN)}` : null;
  if (!at) return job.cron_expression || '';
  const dowList = parseDow(dow);
  if (dom === '*' && mon === '*' && dowList && dowList.length) {
    const label =
      dowList.length === 7
        ? 'Every day'
        : dowList.join(',') === '1,2,3,4,5'
          ? 'Weekdays'
          : dowList.join(',') === '0,6'
            ? 'Weekends'
            : dowList.map((d) => DOW_SHORT[d]).join(', ');
    return `${label} at ${at}${tz}`;
  }
  if (/^\d+$/.test(dom) && mon === '*' && (dow === '*' || dow === '?')) return `Monthly on day ${dom} at ${at}${tz}`;
  if (dom === '*' && mon === '*' && (dow === '*' || dow === '?')) return `Daily at ${at}${tz}`;
  return job.cron_expression || '';
}

export function humanInterval(seconds: number): string {
  if (seconds % 3600 === 0) {
    const h = seconds / 3600;
    return h === 1 ? 'hour' : `${h} hours`;
  }
  const m = Math.round(seconds / 60);
  return m === 1 ? 'minute' : `${m} minutes`;
}

export const DOW_OPTIONS = DOW_SHORT.map((label, value) => ({ label, value }));
